/**
 * Singleton to manage Foundation user logins.
 * @constructor
 * @extends Foundation.Observable
 */
Foundation.Session.Manager = function () {
    Foundation.Observable.call(this);

    /**
     * @private
     * @type {Foundation.Member}
     */
    this._authenticatedMember = null;

    /**
     * @private
     * @type {Foundation.Storable.Bucket}
     */
    this._sessionStorableBucket = null;

    /**
     * @private
     * @type {Foundation.Session.Manager.Delegate}
     */
    this._activeDelegate = null;

    /**
     * @private
     * @type {string}
     */
    this._expectedActiveDelegateId = (new Foundation.Cookie("foundation-sessionManagerDelegate")).getValue();

    /**
     * @private
     * @type {Object.<string,Foundation.Session.Manager.Delegate>}
     */
    this.delegates = {};

    // If a user is logged in, create the Foundation.Member object and
    // assign it to _authenticatedMember.
    var memberOidCookie = new Foundation.Cookie("memberOid");
    if (memberOidCookie.getValue()) {
        this._authenticatedMember = new Foundation.Member(memberOidCookie.getValue());
        this._authenticatedMember.set("userName", (new Foundation.Cookie("username")).getValue());
        this._authenticatedMember.set("avatarUrl", (new Foundation.Cookie("foundation-avatarUrl")).getValue());
    }
};
Foundation.inherit(Foundation.Session.Manager, Foundation.Observable);

/**
 * Storage for the shared login manager singleton.
 * @private
 */
Foundation.Session.Manager._sharedInstance = null;

/**
 * Retreive the shared login manager.
 * @return {Foundation.Session.Manager}
 */
Foundation.Session.Manager.sharedInstance = function () {
    if (!Foundation.Session.Manager._sharedInstance) {
        Foundation.Session.Manager._sharedInstance = new Foundation.Session.Manager();
    }
    return Foundation.Session.Manager._sharedInstance;
};

/**
 * Register a delegate object for the logout.
 * @param {Foundation.Session.Manager.Delegate} delegate
 */
Foundation.Session.Manager.prototype.registerDelegate = function (delegate) {
    this.delegates[delegate.sessionManagerID()] = delegate;
    if (this._expectedActiveDelegateId === delegate.sessionManagerID()) {
        this._activeDelegate = delegate;
        delegate.observeExternalSessionEvents();
    }
    else if (this._expectedActiveDelegateId) {
        delegate.unobserveExternalSessionEvents();
    }
    else {
        delegate.observeExternalSessionEvents();
    }
};

/**
 * Log in the Member.
 * @param {string} delegate
 * @param {Foundation.Member} member
 * @param {?number=} ttl
 */
Foundation.Session.Manager.prototype.logIn = function (delegate, member, ttl) {
    // If we're already logged in somehow, that's a problem, so throw
    // an error.
    if (this.isAuthenticated()) {
        throw new Error("Attempted to call Foundation.Session.Manager.prototype.logIn while already logged in.");
    }

    // Notify all other login delegates to "stand down". In other words,
    // don't listen for external session events (like a user logging into
    // Facebook).
    for (var id in this.delegates) {
        if (id === delegate) continue;
        try { this.delegates[id].unobserveExternalSessionEvents() }
        catch (err) {
          // Login provider JS library probably failed to load, causing
          // an error to be thrown when trying to stop observing session
          // state changes. Just ignore these errors.
        }
    }

    this._authenticatedMember = member;
    this._activeDelegate = this.delegates[delegate];

    var memberOidCookie = new Foundation.Cookie("memberOid");
    if (ttl) memberOidCookie.ttl = ttl;
    memberOidCookie.setValueAndSave(this._authenticatedMember.get("oid"));

    var usernameCookie = new Foundation.Cookie("username");
    if (ttl) usernameCookie.ttl = ttl;
    usernameCookie.setValueAndSave(this._authenticatedMember.get("userName"));

    var avatarUrlCookie = new Foundation.Cookie("foundation-avatarUrl");
    if (ttl) avatarUrlCookie.ttl = ttl;
    avatarUrlCookie.setValueAndSave(this._authenticatedMember.get("avatarUrl"));

    var sessionDelegateCookie = new Foundation.Cookie("foundation-sessionManagerDelegate");
    if (ttl) sessionDelegateCookie.ttl = ttl;
    sessionDelegateCookie.setValueAndSave(delegate);

    // Wait a tick before firing login event. This helps ensure that the caller
    // can clean up after themselves before other observers start messing with
    // the DOM.
    Foundation.Timer.deferral().then(this.fire.bind(this, "login"));
};

/**
 * @return {Foundation.Promise}
 */
Foundation.Session.Manager.prototype.logOut = function () {
    // Verify that we are actually logged in, and throw an error if we aren't.
    if (!this.isAuthenticated()) {
        Foundation.warn("Attempted to call Foundation.Session.Manager.prototype.logOut while already logged out.");
    }

    // Clear out persistent session data.
    (new Foundation.Cookie("memberOid")).erase();
    (new Foundation.Cookie("username")).erase();
    (new Foundation.Cookie("foundation-avatarUrl")).erase();
    (new Foundation.Cookie("foundation-sessionManagerDelegate")).erase();
    if (this.sessionStorableBucket()) this.sessionStorableBucket().clear()

    // Update this instance's state.
    var activeDelegate = this._activeDelegate;
    this._authenticatedMember = null;
    this._activeDelegate = null;

    // Tell the active delegate to log us out, assuming there is one. Otherwise,
    // fall back to telling all the delegates to log out.
    var loggedOut;
    if (activeDelegate) {
        loggedOut = activeDelegate.logOut();
    }
    else {
        // Old, brute-force log out procedure. This will log people
        // out of Facebook even if they are only logged in via Foundation.Login.
        // We need this because we didn't always have the
        // foundation-sessionManagerDelegate cookie to tell us which
        // delegate the user logged in via.
        var delegatePromises = [];
        for (let id in this.delegates) {
            try { delegatePromises.push(this.delegates[id].logOut()) }
            catch (err) {
              // Login provider JS library probably failed to load, causing
              // an error to be thrown when trying to log out. Just ignore
              // these errors.
            }
        }
        loggedOut = Foundation.Promise.fromArray(delegatePromises);
    }

    // Tell nonactive delegates to resume watching for external session events.
    for (let id in this.delegates) {
        var delegate = this.delegates[id];
        if (delegate === activeDelegate) continue;
        try { delegate.observeExternalSessionEvents() }
        catch (err) {
          // Login provider JS library probably failed to load, causing
          // an error to be thrown when trying to resume observing
          // session state changes. Just ignore these errors.
        }
    }

    // Wait a tick before firing logout event. This helps ensure that the caller
    // can clean up after themselves before other observers start messing with
    // the DOM.
    Foundation.Timer.deferral().then(function () {
        loggedOut.then(this.fire.bind(this, "logout"));
    }.bind(this));

    return loggedOut;
};

/**
 * Determine if the user is logged in or not.
 * @return {boolean}
 */
Foundation.Session.Manager.prototype.isAuthenticated = function () {
    return !!(this._authenticatedMember);
};

/**
 * Shows a login dialog if user is not authenticated. The returned promise
 * fulfilled when the user is logged in, and failed if the user dismisses
 * the login dialog without actually logging in.
 * @param {Object.<string,*>=} dialogOptions
 * @return {!Foundation.Promise}
 */
Foundation.Session.Manager.prototype.authenticate = function (dialogOptions) {
    var promise = new Foundation.Promise();

    var showLoginDialog = function () {
        var dialog = new Foundation.Login.Dialog(dialogOptions);
        dialog.closed().then(function () {
            if (this.isAuthenticated()) {
                promise.fulfill();
            }
            else {
                promise.set("errors", ["Log in cancelled."]);
                promise.fail();
            }
        }.bind(this));
    }.bind(this);

    var loginToken = new Foundation.API("GET", "Login/Token", {
      "callerUrl": window.location.toString()
    });
    loginToken.then(function () {
        var member = this.authenticatedMember();
        if (member) {
            if (`${member.get("oid")}` === `${loginToken.get("memberOid")}`) {
                promise.fulfill();
            }
            else {
                showLoginDialog();
            }
        }
        else {
            showLoginDialog();
        }
    }.bind(this));
    loginToken.otherwise(showLoginDialog);

    return promise;
};

Foundation.Session.Manager.prototype.authenticatedMember = function () {
    return this._authenticatedMember;
};

/**
 * @return {Foundation.Storable.Bucket}
 */
Foundation.Session.Manager.prototype.sessionStorableBucket = function () {
    // Only available when we're logged in.
    if (!this.isAuthenticated()) {
        return null
    }

    if (!this._sessionStorableBucket) {
        var ns = `LoggedInMember_${this.authenticatedMember().get("oid")}`
        this._sessionStorableBucket = Foundation.Storable.sessionBucketForNamespace(ns)
    }

    return this._sessionStorableBucket
}

Foundation.Session.Manager.prototype.revalidate = function () {
    for (var id in this.delegates) {
        this.delegates[id].revalidateLogin();
    }
};

// ---------------------------------------------------------------------

window["Foundation"]["Session"]["Manager"] = Foundation.Session.Manager;
Foundation.Session.Manager["sharedInstance"] = Foundation.Session.Manager.sharedInstance;
Foundation.Session.Manager.prototype["isAuthenticated"] = Foundation.Session.Manager.prototype.isAuthenticated;
Foundation.Session.Manager.prototype["authenticate"] = Foundation.Session.Manager.prototype.authenticate;
Foundation.Session.Manager.prototype["logOut"] = Foundation.Session.Manager.prototype.logOut;

// Backwards compatibility.
window["Foundation"]["SessionManager"] = Foundation.Session.Manager;
window["Foundation"]["SessionManager"]["sharedSessionManager"] = Foundation.Session.Manager.sharedInstance;
